import { CLIENT_PAGES } from '@/constants/clientPortalPages'
import type { AddressInfoData } from '@/components/forms/addressInfo'
import { createEmptyAddressInfo } from '@/components/forms/addressInfo'
import type { PersonalInfoData } from '@/components/forms/personalInfo'
import { createEmptyPersonalInfo } from '@/components/forms/personalInfo'
import type { WizardStep } from '@/flows/engine/types'
import {
  addressInfoValidationRules,
  personalInfoValidationRules,
} from '@/utilities/formValidations'

export const CLIENT_KIT_REGISTRATION_STEP_KEYS = [
  'kit-validation',
  'consent',
  'patient-info',
  'address-info',
  'review',
  'success',
] as const

export type ClientKitRegistrationStepKey = (typeof CLIENT_KIT_REGISTRATION_STEP_KEYS)[number]

export type ClientKitRegistrationStep = WizardStep & {
  key: ClientKitRegistrationStepKey
}

export const CLIENT_KIT_REGISTRATION_SUCCESS_STEP = {
  key: 'success',
  name: 'Success',
} as const satisfies ClientKitRegistrationStep

export const CLIENT_KIT_REGISTRATION_FLOW = {
  id: 'client-kit-registration',
  name: 'Kit Registration',
  baseUrl: CLIENT_PAGES.kitRegistration,
  steps: [
    {
      key: 'kit-validation',
      name: 'Kit ID',
    },
    {
      key: 'consent',
      name: 'Consent Agreements',
    },
    {
      key: 'patient-info',
      name: 'Patient Information',
    },
    {
      key: 'address-info',
      name: 'Address Information',
    },
    {
      key: 'review',
      name: 'Review & Submit',
    },
  ] satisfies ClientKitRegistrationStep[],
} as const

export const CLIENT_KIT_REGISTRATION_FIRST_STEP_KEY =
  CLIENT_KIT_REGISTRATION_FLOW.steps[0]?.key ?? 'kit-validation'

export const CLIENT_KIT_REGISTRATION_FIRST_STEP_PATH = CLIENT_KIT_REGISTRATION_FLOW.baseUrl

/**
 * Full step list (including `success`) as a mutable `WizardStep[]`, for mounting
 * `useWizard` (kernel `steps` option requires a mutable array; `CLIENT_KIT_REGISTRATION_FLOW`
 * is a frozen `as const` literal). `success` is included so the kernel's own reconciliation
 * never treats it as an invalid/missing key once the flow completes.
 */
export function getClientKitRegistrationWizardSteps(): ClientKitRegistrationStep[] {
  return [...CLIENT_KIT_REGISTRATION_FLOW.steps, CLIENT_KIT_REGISTRATION_SUCCESS_STEP]
}

/**
 * The reviewable steps only (excludes `success`), as a mutable `WizardStep[]`, for the
 * progress adapter — the Client progress indicator has never counted `success` as a step
 * (see the pre-kernel `KIT_REGISTRATION_PROGRESS_STEP_COUNT`/`KIT_REGISTRATION_STEP_TITLES`
 * constants this replaces).
 */
export function getClientKitRegistrationReviewableSteps(): ClientKitRegistrationStep[] {
  return [...CLIENT_KIT_REGISTRATION_FLOW.steps]
}

export type ClientKitValidationInfo = {
  kitId: string
  validationStatus: 'valid'
  validatedAt: string
  sku?: string
  kitStateIsNy?: boolean
  skipRegistration?: boolean
}

export type ClientKitRegistrationPatientInfo = PersonalInfoData
export type ClientKitRegistrationAddressInfo = AddressInfoData

export type ClientKitRegistrationConsentInfo = {
  atHomeConsentAgreed: boolean
  hipaaConsentAgreed: boolean
  consentedAt?: string
}

export type ClientKitRegistrationFormData = {
  kitInfo?: ClientKitValidationInfo
  patientInfo: ClientKitRegistrationPatientInfo
  addressInfo: ClientKitRegistrationAddressInfo
  consentInfo: ClientKitRegistrationConsentInfo
}

export function isClientKitRegistrationStepKey(
  stepKey: string,
): stepKey is ClientKitRegistrationStepKey {
  return CLIENT_KIT_REGISTRATION_STEP_KEYS.includes(stepKey as ClientKitRegistrationStepKey)
}

export function getClientKitRegistrationStep(stepKey: ClientKitRegistrationStepKey) {
  if (stepKey === 'success') {
    return CLIENT_KIT_REGISTRATION_SUCCESS_STEP
  }
  return CLIENT_KIT_REGISTRATION_FLOW.steps.find((step) => step.key === stepKey)
}

export function getClientKitRegistrationStepIndex(stepKey: ClientKitRegistrationStepKey) {
  if (stepKey === 'success') {
    return CLIENT_KIT_REGISTRATION_FLOW.steps.length
  }
  return CLIENT_KIT_REGISTRATION_FLOW.steps.findIndex((step) => step.key === stepKey)
}

export function createEmptyClientKitRegistrationFormData(): ClientKitRegistrationFormData {
  return {
    patientInfo: createEmptyPersonalInfo(),
    addressInfo: createEmptyAddressInfo(),
    consentInfo: {
      atHomeConsentAgreed: false,
      hipaaConsentAgreed: false,
    },
  }
}

export function isClientKitRegistrationConsentComplete(
  consentInfo: ClientKitRegistrationConsentInfo,
): boolean {
  return consentInfo.atHomeConsentAgreed && consentInfo.hipaaConsentAgreed
}

export const clientKitRegistrationPatientInfoValidationRules =
  personalInfoValidationRules<ClientKitRegistrationPatientInfo>()

export const clientKitRegistrationAddressInfoValidationRules =
  addressInfoValidationRules<ClientKitRegistrationAddressInfo>()
